/**
 * The standalone scene editor's views (spec 029): the scene studio, the
 * scene's Pictures (spec 030) and Under the hood, which one opens, and the
 * per-user remembered view (D-6; Pictures is never remembered, 029 D-12).
 *
 * The remembered view lives in `localStorage` as `sm.sceneView.<userId>`,
 * apart from the story editor's `sm.storyView.<userId>` (025), and is read
 * synchronously so the wrong view never flashes up. Storage that throws
 * reads as nothing remembered; a failed write is ignored.
 */

import { readPreference, writePreference } from './storyView';

export { sessionUserId } from './storyView';

export type SceneView = 'studio' | 'pictures' | 'hood';
/** The views a scene opens in from storage (Pictures only from the URL). */
export type RememberedSceneView = 'studio' | 'hood';

export const sceneViewKey = (userId: number) => `sm.sceneView.${userId}`;

export const isSceneView = (v: unknown): v is SceneView => v === 'studio' || v === 'pictures' || v === 'hood';
const isRemembered = (v: unknown): v is RememberedSceneView => v === 'studio' || v === 'hood';

export function readStoredSceneView(userId: number): RememberedSceneView | null {
  const v = readPreference(sceneViewKey(userId));
  return isRemembered(v) ? v : null;
}

/** Only the view switch calls this (opening a scene never writes); Pictures is never written. */
export function writeStoredSceneView(userId: number, view: SceneView): void {
  if (!isRemembered(view)) return;
  writePreference(sceneViewKey(userId), view);
}

export interface ResolveSceneViewInput {
  /** The URL's `view` query, if any. */
  query: string | null | undefined;
  userId: number;
  /** A brief from the scene start sheet is waiting for this scene. */
  pendingBrief: boolean;
}

/**
 * Which view opens: a pending brief always opens the studio; then a `view`
 * query of `studio`, `pictures` or `hood`; then the remembered view; then the studio
 * (D-7). Reads storage, never writes it.
 */
export function resolveSceneView({ query, userId, pendingBrief }: ResolveSceneViewInput): SceneView {
  if (pendingBrief) return 'studio';
  if (isSceneView(query)) return query;
  return readStoredSceneView(userId) ?? 'studio';
}
